import _ from 'lodash';
import queryString from 'query-string';
import Authenticator from '../protocol/Authenticator';
import Storage from '../protocol/Storage';
import ConnectionUtil from './session/ConnectionUtil';
import System from '../basic/System';

export default class ParamsUtil {
  /**
   *
   * @param url{string}
   * @param object{*} Key and Values inside the objects are appended to url as query params.
   */
  static append(url, object) {
    const addQuestionMark: boolean = url.indexOf('?') === -1;
    if (addQuestionMark) {
      url = url + '?';
    }
    let appendAmpersand = true;
    if (addQuestionMark || url.charAt(url.length - 1) === '&') {
      appendAmpersand = false;
    }

    if (object)
      _.forEach(object, function(value, key) {
        if (value) {
          //No need to set null, undefined values or default values like boolean, they will be false by default.
          if (appendAmpersand) {
            url = url + '&';
          }
          url = url + key + '=' + encodeURIComponent(value); // + '&';
          appendAmpersand = true;
        }
      });
    return url;
  }

  static appendJobId(url, jobId) {
    return ParamsUtil.append(url, {
      jobId: jobId
    });
  }

  static appendResolution(url, resolution) {
    return ParamsUtil.append(url, { resolution: resolution });
  }

  static appendMediaType(url, type) {
    if (!type) return url;
    return ParamsUtil.append(url, { mediaType: type });
  }

  static appendPageNumber(url, pageNumber) {
    if (!pageNumber) return url;
    return ParamsUtil.append(url, { pageNumber: pageNumber });
  }

  /**
   * @param url{string}
   * @param repoItem{repoItem}
   * @return {string}
   */
  static appendFuidBidRrn(url, bid, fuid, rrn, name) {
    let extension = name.split('.');
    extension = extension[extension.length - 1];
    let data = {
      fuid: fuid,
      bid: bid,
      rrn: rrn,
      fileName: name
    };
    if ('pdf' == extension) {
      data.format = 'pdf';
      data.disableDownload = true;
      data.cache = true;
    }

    return ParamsUtil.append(url, data);
  }

  /**
   *
   * @param {string} url
   * @param {number} bid
   * @param {number} fuid
   * @param {number} frn
   * @param {number} rrn
   * @param {number} name
   */
  static appendFuidBidRrnFrn(url, bid, fuid, frn, rrn, name) {
    if (rrn <= 0 || frn <= 0) {
      //throw new RangeError("RRN or FRN can't be less than or equal to zero");
    }
    if (name == null) {
      name = '';
    }

    return ParamsUtil.append(url, {
      fuid: fuid,
      bid: bid,
      frn: frn,
      rrn: rrn,
      fileName: name, //Todo: remove this field
      fqPath: name
    });
  }

  static appendFuidBidRrnFrnCollectionId(url, bid, fuid, frn, rrn, name, collectionId) {
    url = ParamsUtil.appendFuidBidRrn(url, bid, fuid, rrn, name);
    return ParamsUtil.appendCollectionId(url, collectionId);
  }

  static appendCollectionId(url, collectionId) {
    return ParamsUtil.append(url, {
      collectionId: collectionId
    });
  }

  static appendURLToDownloadThisPath(url, path) {
    return ParamsUtil.append(url, {
      downloadThisPath: path,
      app: 'webclient',
      forceDownloadAsAttachment: true
    });
  }

  static appendLogoPicTag() {
    const url = ConnectionUtil.getPreviewServerUrl() + '/webclient/api/images/logo';
    return ParamsUtil.append(url, {
      getLogoPic: true,
      app: 'webclient'
    });
  }

  static generateWebclientURL(asset) {
    const url = ConnectionUtil.getActualBaseURL() + '/webclient?activeTab=ASSETS';
    return ParamsUtil.append(url, {
      fuid: asset.getFuid()
    });
  }

  static appendHomeHeaderPicTag() {
    const url = ConnectionUtil.getPreviewServerUrl() + '/webclient/api/images/homeheader';
    return ParamsUtil.append(url, {
      app: 'webclient'
    });
  }

  static appendProfilePicTag(url, username) {
    return ParamsUtil.append(url, {
      getProfilePic: true,
      user: username
    });
  }

  static createAuthHeaders() {
    return {
      /* zm_repo_id: Authenticator.AUTH_DATA.repoId,
       zm_username: Authenticator.AUTH_DATA.username,
       zm_cookie: Authenticator.AUTH_DATA.cookie,
       zm_server: Authenticator.AUTH_DATA.serverUrl,
       zm_web_session: Authenticator.AUTH_DATA.sessionId,
       zm_web_client: true,
       zm_app_name: "webclient" */
    };
  }

  static appendAuthData(url) {
    return url;
    /* return ParamsUtil
     .append(url, {
     zm_repo_id: Authenticator.AUTH_DATA.repoId,
     zm_username: Authenticator.AUTH_DATA.username,
     zm_cookie: Authenticator.AUTH_DATA.cookie,
     zm_server: Authenticator.AUTH_DATA.serverUrl,
     zm_web_session: Authenticator.AUTH_DATA.sessionId,
     zm_web_client: true,
     zm_app_name: "webclient"
     }); */
  }

  static getAuthData() {
    return {
      zm_server: Authenticator.AUTH_DATA.serverUrl,
      zm_repo_id: Authenticator.AUTH_DATA.repoId,
      zm_cookie: Authenticator.AUTH_DATA.cookie,
      zm_username: Authenticator.AUTH_DATA.username,
      zm_app_name: System.getAppName()
    };
  }

  static appendOpcode(url, opcode) {
    return ParamsUtil.append(url, {
      opCode: opcode
    });
  }

  /**
   * @param url {String}
   * @param username {String}
   * @returns {*}
   */
  static appendUserName(url, username) {
    return ParamsUtil.append(url, {
      user: username
    });
  }

  static appendStateParams(newParams) {
    const params = window.ARGS;

    // As our urls get more complex add more values here.
    if (params.collection) {
      newParams.collection = params.collection;
    }

    const str = queryString.stringify(newParams);
    return `?${str}`;
  }

  static convertObjectToUrlEncodedFormData(data): ?string {
    let formBody = undefined;
    if (data) {
      formBody = [];
      for (const property in data) {
        if (data[property] !== undefined && data[property] !== null) {
          const encodedKey = encodeURIComponent(property);
          const encodedValue = encodeURIComponent(data[property]);
          formBody.push(encodedKey + '=' + encodedValue);
        }
      }
    }
    return formBody ? formBody.join('&') : undefined;
  }

  /**
   *
   * @param {String} url  URL to which data has to be submitted as form url encoded data
   * @param {*} data Object containing key value data.
   * @returns {Promise.<Object>} Returns a project, whose success is json object
   */
  static submitAsFormUrlEncoded(url, data, headers: { [key: string]: string } = {}) {
    const formBody = data ? ParamsUtil.convertObjectToUrlEncodedFormData(data) : data;
    const allHeaders = formBody
      ? {
          'Content-Type': 'application/x-www-form-urlencoded',
          ...headers
        }
      : headers;

    return fetch(url, {
      method: formBody ? 'POST' : 'GET',
      mode: 'no-cors',
      cache: 'no-cache',
      credentials: 'include', //Include cookies for CORs request
      headers: allHeaders,
      body: formBody
    }).then((response) => {
      const contentType = response.headers.get('content-type');
      if (response.status === 200) {
        if (contentType && contentType.indexOf('application/json') !== -1) {
          return response.json();
        } else {
          return response.text();
        }
      } else {
        if (contentType && contentType.indexOf('application/json') !== -1) {
          return response.json();
        } else {
          return response.text();
        }
      }
    });
  }

  static sendAuthRequestOnReviewServer(formData, url: ?string) {
    if (!formData || !url) {
      if (Authenticator.AUTH_DATA && !_.isEmpty(Authenticator.AUTH_DATA && Authenticator.AUTH_DATA.previewServerUrl)) {
        url = ConnectionUtil.getReviewAuthUrl();
        formData = {
          zm_username: Authenticator.AUTH_DATA.username,
          thiscommandonly: true,
          extclient: true,
          browserurl: ConnectionUtil.getBaseUrl(),
          zm_app_name: 'webclient',
          zm_repo_id: Authenticator.AUTH_DATA.repoId,
          zm_cookie: Authenticator.AUTH_DATA.cookie,
          zm_web_session: Storage.get('zm_web_session')
        };
      }
    }
    if (formData && url) {
      return new Promise((resolve, reject) => {
        const promise = ParamsUtil.submitAsFormUrlEncoded(url, formData);
        promise
          .then(function(data) {
            resolve(data);
          })
          .catch(function(error) {
            reject(error);
          });
      });
    }
  }
  //If Fuids & RRNS are in Array Format
  static fuidsRrnsBidUrl(url: String, fuids: Array, rrns: Array, bid: Number) {
    if (fuids.length != rrns.length) {
      return null;
    }
    let str = '?';
    for (let i = 0; i < fuids.length; i++) {
      str += '&fuid=' + fuids[i] + '&rrn=' + rrns[i];
    }
    str += '&bid=' + bid;
    return url + str;
  }
}
